import useMedia from '@charlietango/use-media'
import useWindowSize from '@charlietango/use-window-size'
import cx from 'classnames'
import * as React from 'react'
import Slider from 'react-slick'

import type { CarouselCardViewModel } from '../../view-models/CarouselCardViewModel'
import { trackEvent } from '../../utils/gtag'
import useLockScroll from '../Carousel/useLockScroll'
import CarouselCard from '../CarouselCard/CarouselCard'
import Image from '../Image/Image'
import styles from './carouselDesktop-style.css'

type Props = {
  items: Array<CarouselCardViewModel>
  useAsHeader?: boolean
  autoplay?: boolean
  autoplaySpeed?: number
}

const CarouselDesktop = ({ items, useAsHeader, autoplay, autoplaySpeed }: Props) => {
  const [eventIsSaved, saveEvent] = React.useState(false)
  const isDesktop = useMedia({
    minWidth: 992,
  })
  const { width } = useWindowSize()
  const imageSlider: React.MutableRefObject<any> = React.useRef()
  const mainSlider: React.MutableRefObject<any> = React.useRef()
  const lockRef: React.MutableRefObject<any> = useLockScroll()

  const trackArrowClickOnce = () => {
    if (!eventIsSaved) {
      const event = window.site_section === 'pension' ? 'karrusel' : 'modules-interaction'
      const eventData = window.site_section === 'pension' ? 'arrow-click' : 'karrusel'
      trackEvent({
        event,
        eventData,
      })
      saveEvent(true)
    }
  }

  const handleNext = () => {
    imageSlider?.current?.slickNext()
    mainSlider?.current?.slickNext()
    trackArrowClickOnce()
  }

  const handlePrevious = () => {
    imageSlider?.current?.slickPrev()
    mainSlider?.current?.slickPrev()
    trackArrowClickOnce()
  }

  const mainSliderSettings = {
    dots: true,
    arrows: false,
    speed: 500,
    infinite: true,
    autoplay: autoplay,
    autoplaySpeed: autoplaySpeed || 3000,
    pauseOnHover: false,
    pauseOnFocus: false,
    adaptiveHeight: false,
    draggable: false,
  }
  const imageSliderSettings = {
    arrows: false,
    speed: 500,
    infinite: true,
    autoplay: autoplay,
    autoplaySpeed: autoplaySpeed || 3000,
    pauseOnHover: false,
    pauseOnFocus: false,
    draggable: false,
  }

  if (!items) return null

  return (
    <>
      {isDesktop ? (
        <div className={cx(styles.slickContainer, styles.imageSlickContainer)}>
          <Slider ref={imageSlider} {...imageSliderSettings} swipe={width < 992} key="imageSlider">
            {items.map((item, i) => {
              return item.image ? (
                <div key={`img_${i}`}>
                  <div className={styles.carouselCard}>
                    {item.link ? (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a
                        aria-hidden="true"
                        tabIndex={-1}
                        href={item.link.href}
                        target={item.link.target}
                        className={styles.imgLink}
                      />
                    ) : null}
                    <Image
                      {...item.image}
                      className={styles.slideImage}
                      src={item.image.src}
                      width={775}
                      height={500}
                    />
                  </div>
                </div>
              ) : (
                <div
                  key={`img_${i}`}
                  style={{
                    height: '0',
                    paddingBottom: `${(500 / 575) * 100}%`,
                  }}
                />
              )
            })}
          </Slider>
        </div>
      ) : (
        <div className={cx(styles.slickContainer, styles.imageSlickContainer)} />
      )}
      <div className={cx(styles.slickContainer, styles.mainSlickContainer)} ref={lockRef}>
        <Slider
          ref={mainSlider}
          {...mainSliderSettings}
          draggable={width < 992}
          swipe={width < 992}
          key="mainSlider"
          style={{ padding: '0' }}
        >
          {items.map((item, i) => (
            <CarouselCard
              item={item}
              key={i}
              width={width}
              cardIndex={i}
              className={cx(
                styles.innerCard,
                items.length < 2 && styles.singleCard,
              )}
              useAsHeader={useAsHeader}
            />
          ))}
        </Slider>

        {items.length > 1 && (
          <div className={styles.navHolder}>
            <button
              aria-label="prev"
              className={cx('slick-prev', styles.navBtn)}
              onClick={handlePrevious}
            />
            <button
              aria-label="next"
              className={cx('slick-next', styles.navBtn)}
              onClick={handleNext}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default CarouselDesktop
